<script setup lang="ts">
import type { ArticleStoryblok } from '~/component-types-sb'
import { removeEmojis } from '~/shared/utils/removeEmojis'

interface TTag {
  id: number
  name: string
  value: string
  dimension_value?: any
}

const props = defineProps<{
  blok: ArticleStoryblok
  tag: TTag
}>()

const itemRefs = ref([])
const anchorLinks = ref([])
const target = ref<null | HTMLElement>(null)
const lastPosts = ref<null | object>(null)
const isAuditVisible = useState('isAuditVisible', () => useElementVisibility(target))

const storyblokApi = useStoryblokApi()
const { public: { NUXT_PUBLIC_IS_PRODUCTION } } = useRuntimeConfig()

// TODO: remove when fixed
if (!NUXT_PUBLIC_IS_PRODUCTION) {
  lastPosts.value = (await storyblokApi.get('cdn/stories', {
    version: useRoute().query._storyblok ? 'draft' : 'published',
    starts_with: 'blog',
    sort_by: 'content.created_at:desc',
    per_page: '6',
    is_startpage: false,
    filter_query: {
      tag: {
        in: props.tag.value,
      },
      _uid: {
        not_in: props.blok._uid,
      },
    },
  })).data
}

function decodeHtmlEntities(text: string) {
  return text?.replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, '\'')
    .replace(/&mdash;/g, '—')
    .replace(/&ndash;/g, '–')
    .replace(/&copy;/g, '©')
    .replace(/&reg;/g, '®')
    .replace(/&trade;/g, '™')
    .replace(/&laquo;/g, '«')
    .replace(/&raquo;/g, '»')
}
function formatDate(date: string) {
  return new Date(date).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).replaceAll('.', '').slice(0, -2)
}
function test(ref) {
  const el = document.getElementById(ref)
  el.scrollIntoView({ behavior: 'smooth' })
}

onMounted(() => {
  if (props.blok.test) {
    anchorLinks.value = props.blok.test.map(item => ({
      blok: item,
    }),
    )
  }
})
watchEffect(() => {
  isAuditVisible.value = useElementVisibility(target)
})

useHead({ title: decodeHtmlEntities(props?.blok?.title) })
useServerSeoMeta({
  description: props?.blok?.description,
  image: props.blok.article_img?.filename,
  title: decodeHtmlEntities(props?.blok?.title),
})
</script>

<template>
  <div v-editable="blok" class="relative">
    <div class="bg-milky-200 text-primary">
      <div class="max-w-[1012px] mx-auto px-5 md:px-4 pt-8 md:pt-14 pb-16 md:pb-24">
        <div class="flex items-center gap-2 mb-2">
          <NuxtLink
            class="cursor-pointer text-m-primary-small text-blue-500 hover:text-blue-400 hover:decoration-blue-200 underline underline-offset-4 decoration-blue-300 transition-all"
            to="/"
          >
            Главная
          </NuxtLink>
          <NuxtImg
            quality="70"
            class="mt-1"
            src="/breadcrumb-arrow-right.svg"
            alt="breadcrumb-arrow-right"
          />
          <NuxtLink
            class="cursor-pointer text-m-primary-small text-blue-500 hover:text-blue-400 hover:decoration-blue-200 underline underline-offset-4 decoration-blue-300 transition-all"
            to="/blog"
          >
            Блог
          </NuxtLink>
          <NuxtImg
            quality="70"
            class="mt-1"
            src="/breadcrumb-arrow-right.svg"
            alt="breadcrumb-arrow-right"
          />
          <NuxtLink
            class="cursor-pointer text-m-primary-small text-blue-500 hover:text-blue-400 hover:decoration-blue-200 underline underline-offset-4 decoration-blue-300 transition-all"
            :to="`/blog?tag=${tag.value}`"
          >
            {{ removeEmojis(tag.name) }}
          </NuxtLink>
          <NuxtImg
            quality="70"
            class="mt-1"
            src="/breadcrumb-arrow-down.svg"
            alt="breadcrumb-arrow-down"
          />
        </div>

        <h1 v-if="blok.title" class="md:text-h1 text-m-h1 font-bold mb-4" v-html="blok?.title" />
        <div class="text-primary-large" v-html="props.blok.content" />

        <div class="hidden" itemprop="author" itemscope itemtype="http://schema.org/Person">
          <img :alt="blok.author" itemprop="image" :src="blok?.author_img?.filename">
          <span itemprop="name" v-text="blok.author" />
          <span itemprop="jobTitle">Должность</span>
        </div>

        <div class="flex items-center gap-3 mt-4">
          <div v-if="blok?.author_img?.filename" class="rounded-full">
            <NuxtImg placeholder provider="storyblok" quality="100" width="48" height="48" class="rounded-full" :src="blok?.author_img?.filename" alt="author image" />
          </div>
          <div>
            <div class="text-primary-small" v-html="blok?.author" />
            <div class="flex gap-3 text-primary-small text-gray-500">
              <p class="text-[16px]">
                {{ new Date(blok.created_at).toLocaleDateString('ru-RU',
                                                                { day: '2-digit',
                                                                  month: 'short',
                                                                  year: 'numeric',
                                                                }).replaceAll('.', '').slice(0, -2) }}
              </p>
              <!-- <div class="flex items-center gap-1">
                <Icon name="meteor-icons:eye" class="text-[12px] mt-0.5" />
                <p>81</p>
              </div> -->
              <div v-if="blok.reading_time" class="flex items-center gap-1">
                <Icon name="meteor-icons:clock" class="text-[12px] mt-0.5" />
                {{ blok.reading_time }}
              </div>
            </div>
          </div>
        </div>
        <NuxtImg
          v-if="blok?.article_img"
          placeholder
          preload
          loading="lazy"
          provider="storyblok"
          :src="blok?.article_img?.filename"
          class="mx-auto rounded-2xl mt-12 w-full"
          :alt="blok?.article_img?.alt"
        />
      </div>
    </div>
    <div class="flex flex-col md:block">
      <div v-if="anchorLinks.length > 0 && blok.show_table" class="px-5 max-w-[1012px] md:h-0 md:sticky md:top-0 md:mx-auto flex">
        <div :class="[isAuditVisible ? 'opacity-0 pointer-events-none' : 'opacity-100 pointer-events-auto']" class="transition-all duration-300 mt-12 md:ml-auto mr-2">
          <h3 class="text-m-h3 md:text-h3 mb-2">
            Содержание
          </h3>
          <div v-for="ref in anchorLinks" :key="ref" class="text-primary-16 max-w-1/3 md:max-w-[240px] cursor-pointer mt-4 md:text-primary text-blue-500 underline decoration-blue-300 hover:decoration-blue-500 decoration-dashed underline-offset-4" @click="test(ref.blok._uid)" v-html="ref?.blok?.title" />
        </div>
      </div>
      <div class="w-full flex justify-center items-center">
        <div class="duration-150" :class="blok.show_table ? 'w-full lg:w-[950px] lg:pr-[250px]' : 'w-full lg:w-[1015px]'">
          <StoryblokComponent
            v-for="feature in blok.test" ref="itemRefs" :key="feature.id"
            :blok="feature"
          />
        </div>
      </div>

      <div ref="target" />

      <div v-if="lastPosts" class="w-full flex justify-center">
        <div class="w-full md:w-[1012px] mb-10">
          <h2 class="text-center md:text-left md:text-h2 text-m-h2 mb-10" v-text="'Ещё в тему'" />
          <div class="w-full flex flex-col md:flex-row gap-5 md:gap-0 items-center justify-between">
            <template
              v-for="(post, idx) in lastPosts.stories"
              :key="post.id"
            >
              <NuxtLink
                v-if="idx > 2"
                class="w-[330px] h-full flex flex-col gap-4 cursor-pointer hover:opacity-80 duration-150"
                :to="`/${post.full_slug}`"
              >
                <NuxtImg
                  width="330"
                  height="180"
                  provider="storyblok"
                  :modifiers="{ smart: true }"
                  :alt="`photo of article: ${post.content.title}`"
                  class="rounded-xl border w-full h-[200px] object-cover"
                  :src="post.content.article_img.filename"
                />
                <h3 class="md:text-h3 md:text-lg text-m-primary-large underline decoration-1 decoration-secondary-300 underline-offset-4" v-html="post.content.title" />
                <div class="flex justify-between items-center">
                  <div class="md:px-4 px-3 py-[6px] bg-secondary-100 rounded-2xl text-m-primary-small" v-text="removeEmojis(tag.name)" />
                  <div class="flex gap-2 text-m-primary-small text-secondary-500">
                    <p v-text="formatDate(post.content.created_at)" />

                    <div v-if="post.content.views" class="flex items-center gap-0.5">
                      <Icon name="meteor-icons:eye" class="text-[12px]" />
                      <p v-text="post.content.views" />
                    </div>

                    <div v-if="post.content.reading_time" class="flex items-center gap-0.5">
                      <Icon name="meteor-icons:clock" class="text-[12px]" />
                      <p v-text="post.content.reading_time" />
                    </div>
                  </div>
                </div>
              </NuxtLink>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
